import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_COMMITTEEMEETING_LOAD_BY_ID,
    ACTION_COMMITTEEMEETING_LOAD_BY_COMMITTEE_ID,
    ACTION_COMMITTEEMEETING_LOAD_FAILURE,
    ACTION_COMMITTEEMEETING_LOAD_SUCCESS,

    ACTION_COMMITTEEMEETING_SAVE,
    ACTION_COMMITTEEMEETING_SAVE_SUCCESS,
    ACTION_COMMITTEEMEETING_SAVE_FAILURE,

    ACTION_COMMITTEEMEETING_CREATE,
    ACTION_COMMITTEEMEETING_CREATE_SUCCESS,
    ACTION_COMMITTEEMEETING_CREATE_FAILURE,

    ACTION_COMMITTEEMEETING_VALIDATE,
    ACTION_COMMITTEEMEETING_VALIDATE_SUCCESS,
    ACTION_COMMITTEEMEETING_VALIDATE_FAILURE,

    ACTION_COMMITTEEMEETING_CLEAR,
    ACTION_COMMITTEEMEETING_FILTER,

    CommitteeMeetingAction
} from '../actions/committeeMeeting';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ICommitteeMeetingState {
    formData: Dtos.CommitteeMeeting | Dtos.CommitteeMeeting[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.CommitteeMeeting[];
    formLength: number;
}

const initialState: ICommitteeMeetingState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ICommitteeMeetingState, _isLoading: boolean): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ICommitteeMeetingState,
    formData: Dtos.CommitteeMeeting | Dtos.CommitteeMeeting[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ICommitteeMeetingState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ICommitteeMeetingState, formFilter: IFormFilter): ICommitteeMeetingState {

    const items: Dtos.CommitteeMeeting[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.location && p.location.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: ICommitteeMeetingState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ICommitteeMeetingState, propertiesToClear?: string[]): ICommitteeMeetingState {

    if (!propertiesToClear) {
        const newState: ICommitteeMeetingState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ICommitteeMeetingState, _isSaving: boolean): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ICommitteeMeetingState,
    formData: Dtos.CommitteeMeeting | Dtos.CommitteeMeeting[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ICommitteeMeetingState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ICommitteeMeetingState, _isCreating: boolean): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ICommitteeMeetingState,
    formData: Dtos.CommitteeMeeting,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ICommitteeMeetingState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ICommitteeMeetingState, _isValidating: boolean): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ICommitteeMeetingState,
    formData: Dtos.CommitteeMeeting,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ICommitteeMeetingState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingState {
    const newState: ICommitteeMeetingState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const CommitteeMeetingReducer: Reducer<ICommitteeMeetingState> = (state: ICommitteeMeetingState = initialState, action: CommitteeMeetingAction) => {
    switch (action.type) {
        case ACTION_COMMITTEEMEETING_LOAD_BY_ID:
        case ACTION_COMMITTEEMEETING_LOAD_BY_COMMITTEE_ID:
            return loading(state, true);
        case ACTION_COMMITTEEMEETING_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_COMMITTEEMEETING_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETING_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_COMMITTEEMEETING_SAVE:
            return saving(state, true);
        case ACTION_COMMITTEEMEETING_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEETING_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETING_CREATE:
            return creating(state, true);
        case ACTION_COMMITTEEMEETING_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_COMMITTEEMEETING_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETING_VALIDATE:
            return validating(state, true);
        case ACTION_COMMITTEEMEETING_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEETING_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETING_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default CommitteeMeetingReducer;